import React, {
  ChangeEvent,
  DragEvent,
  FC,
  ReactNode,
  useCallback,
  useRef,
  useState,
} from "react";
import Button from "components/Button/Button";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { IconType } from "enums/Icon";
import { PreLoaderColor } from "enums/PreLoader";
import {
  Zone,
  ZoneHint,
  ZoneIcon,
  ZoneSpinner,
  ZoneText,
  ZoneTitle,
} from "./FileDropZone.style";

interface FileDropZoneProps {
  onFile: (file: File) => void;
  accept: string;
  text: string;
  buttonText: string;
  hint: ReactNode;
  // Busy: a spinner replaces the icon and files dropped or picked meanwhile
  // are ignored.
  isLoading?: boolean;
  className?: string;
}

// One file, dropped or picked; the caller decides what the file is for.
// `accept` only filters the picker, so the caller still checks what it gets.
const FileDropZone: FC<FileDropZoneProps> = ({
  onFile,
  accept,
  text,
  buttonText,
  hint,
  isLoading = false,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const takeFile = useCallback(
    (file?: File | null) => {
      if (file && !isLoading) onFile(file);
    },
    [isLoading, onFile],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files ? event.target.files[0] : null;
      // reset synchronously so re-selecting the same file re-fires onChange
      event.target.value = "";
      takeFile(file);
    },
    [takeFile],
  );

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    // dragleave also fires when the pointer moves between the zone's children
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      // even while busy, or the browser would open the dropped file
      event.preventDefault();
      setIsDragging(false);
      takeFile(event.dataTransfer?.files?.[0]);
    },
    [takeFile],
  );

  return (
    <Zone
      data-testid="file-dropzone"
      className={className}
      $isDragging={isDragging && !isLoading}
      onDragEnter={handleDragOver}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={handleChange}
      />
      <ZoneTitle>
        {isLoading ? (
          <ZoneSpinner isActive color={PreLoaderColor.Primary} />
        ) : (
          <ZoneIcon type={IconType.FileCSV} />
        )}
        <ZoneText>{text}</ZoneText>
      </ZoneTitle>
      <Button
        type={ButtonType.Button}
        variant={ButtonVariant.Bordered}
        color={ButtonColor.Primary}
        disabled={isLoading}
        onClick={() => inputRef.current?.click()}
      >
        {buttonText}
      </Button>
      <ZoneHint>{hint}</ZoneHint>
    </Zone>
  );
};

export default FileDropZone;
